import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * Hook to monitor session validity and handle auth errors globally
 */
export const useSessionMonitor = () => {
  const { user, session } = useAuth();
  const lastCheckRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Only check if we think we have a session
        if (!session) return;

        // Check if session is still valid
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error || !currentSession) {
          console.error('Session check failed:', error);
          // Force logout on session error
          await supabase.auth.signOut();
          window.location.href = '/login';
          return;
        }

        // Check if session is about to expire (within 5 minutes)
        if (currentSession.expires_at) {
          const expiresAt = currentSession.expires_at * 1000;
          const timeUntilExpiry = expiresAt - Date.now();
          
          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log('Session expiring soon, refreshing...');
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('Failed to refresh session:', refreshError);
              await supabase.auth.signOut();
              window.location.href = '/login';
            }
          }
        }

        lastCheckRef.current = Date.now();
      } catch (error) {
        console.error('Session monitor error:', error);
      }
    };

    // Check session every 2 minutes
    checkIntervalRef.current = setInterval(checkSession, 2 * 60 * 1000);

    // Initial check
    checkSession();

    // Listen for visibility changes - check session when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastCheckRef.current > 60 * 1000) {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('Network connection restored, checking session...');
      checkSession();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [session]);

  return { user, session };
};
